import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { FeatureCollection } from 'geojson';
import { useMap } from '../map/MapContext';
import { DrawingEngine, type DrawingType, type DrawingFeatureRecord } from '../map/drawing-engine';
import { getUserData, storeUserData } from '../services/CacheService';

const TOOLS: { tool: DrawingType; icon: string; label: string; hint: string }[] = [
  { tool: 'point',       icon: '📍', label: 'Point',     hint: 'Click to place a point' },
  { tool: 'line',        icon: '📏', label: 'Line',      hint: 'Click to add vertices, double-click to finish' },
  { tool: 'polygon',     icon: '⬡',  label: 'Polygon',   hint: 'Click to add vertices, double-click to close' },
  { tool: 'circle',      icon: '⭕', label: 'Circle',    hint: 'Click center, then click to set radius' },
  { tool: 'rectangle',   icon: '▭',  label: 'Rectangle', hint: 'Click first corner, then opposite corner' },
  { tool: 'freehand',    icon: '✍️', label: 'Freehand',  hint: 'Click and drag to draw freehand' },
  { tool: 'arrow',       icon: '➡️', label: 'Arrow',     hint: 'Click vertices, double-click to finish' },
  { tool: 'text',        icon: 'Aa',  label: 'Text',      hint: 'Click to place a text label' },
  { tool: 'measurement', icon: '📐', label: 'Measure',   hint: 'Click vertices, double-click to finish — shows distance/bearing' },
];

const COLORS = ['#F59E0B', '#EF4444', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#F1F5F9'];
const DRAWINGS_METADATA_KEY = 'drawings';

export const DrawingPanel: React.FC = () => {
  const { map } = useMap();
  const engineRef = useRef<DrawingEngine | null>(null);
  const [activeTool, setActiveTool] = useState<DrawingType | null>(null);
  const [features, setFeatures] = useState<DrawingFeatureRecord[]>([]);
  const [activeColor, setActiveColor] = useState(COLORS[0]);
  const [undoStack, setUndoStack] = useState<DrawingFeatureRecord[][]>([]);

  // Initialize engine when map is ready
  useEffect(() => {
    if (!map) return;
    const engine = new DrawingEngine(map, (updatedFeatures) => {
      setFeatures([...updatedFeatures]);
      void storeUserData<FeatureCollection>(DRAWINGS_METADATA_KEY, {
        type: 'FeatureCollection',
        features: updatedFeatures.map((feature) => feature.feature),
      }).catch(() => { /* persistence is best effort */ });
    });
    engine.init();
    engineRef.current = engine;
    let cancelled = false;
    void getUserData<FeatureCollection>(DRAWINGS_METADATA_KEY).then((stored) => {
      if (!cancelled && stored?.features.length) engine.importGeoJSON(stored);
    }).catch(() => { /* first run or unavailable IndexedDB */ });
    return () => { cancelled = true; engine.destroy(); engineRef.current = null; };
  }, [map]);

  const selectTool = useCallback((tool: DrawingType) => {
    const engine = engineRef.current;
    if (!engine) return;
    if (activeTool === tool) {
      engine.deactivate();
      setActiveTool(null);
    } else {
      engine.activate(tool, activeColor);
      setActiveTool(tool);
    }
  }, [activeTool, activeColor]);

  const changeColor = useCallback((color: string) => {
    setActiveColor(color);
    engineRef.current?.setColor(color);
  }, []);

  const removeFeature = useCallback((id: string) => {
    const engine = engineRef.current;
    if (!engine) return;
    setUndoStack((prev) => [...prev, engine.getFeatures()]);
    engine.removeFeature(id);
  }, []);

  const undo = useCallback(() => {
    const engine = engineRef.current;
    if (!engine || undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    engine.clearAll();
    engine.importGeoJSON({
      type: 'FeatureCollection',
      features: prev.map((f) => f.feature),
    });
    setUndoStack((s) => s.slice(0, -1));
  }, [undoStack]);

  const clearAll = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    setUndoStack((prev) => [...prev, engine.getFeatures()]);
    engine.clearAll();
    engine.deactivate();
    setActiveTool(null);
  }, []);

  const exportGeoJSON = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    const fc = engine.exportGeoJSON();
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'drawings.geojson'; a.click();
    URL.revokeObjectURL(url);
  }, []);

  const importGeoJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const text = await file.text();
      try {
        const fc = JSON.parse(text);
        engineRef.current?.importGeoJSON(fc);
      } catch (err) { console.error('Invalid GeoJSON:', err); }
    };
    input.click();
  }, []);

  const activeHint = TOOLS.find((t) => t.tool === activeTool)?.hint;

  return (
    <div>
      {/* Instruction hint */}
      {activeHint && (
        <div className="panel-section" style={{ background: 'rgba(59,130,246,0.1)', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: 'var(--accent-light)' }}>
          💡 {activeHint}
        </div>
      )}

      {/* Color picker */}
      <div className="panel-section">
        <div className="panel-section-title">Color</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => changeColor(c)}
              style={{
                width: 24, height: 24, borderRadius: '50%', background: c, border: c === activeColor ? '3px solid #fff' : '2px solid var(--border)',
                cursor: 'pointer', padding: 0,
              }}
              title={c}
            />
          ))}
        </div>
      </div>

      {/* Tool grid */}
      <div className="panel-section">
        <div className="panel-section-title">Drawing Tools</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
          {TOOLS.map((t) => (
            <button
              key={t.tool}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 4px',
                background: activeTool === t.tool ? '#1E3A5F' : 'transparent',
                borderColor: activeTool === t.tool ? 'var(--accent)' : 'var(--border)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
                color: activeTool === t.tool ? 'var(--accent-light)' : 'var(--text-secondary)',
              }}
              onClick={() => selectTool(t.tool)}
              title={t.hint}
            >
              <span style={{ fontSize: 18 }}>{t.icon}</span>
              <span style={{ fontSize: 10 }}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="panel-section">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={undo} disabled={undoStack.length === 0}>↩ Undo</button>
          <button className="btn btn-secondary" onClick={clearAll} disabled={features.length === 0}>🗑️ Clear</button>
          <button className="btn btn-secondary" onClick={exportGeoJSON} disabled={features.length === 0}>📄 Export</button>
          <button className="btn btn-secondary" onClick={importGeoJSON}>📂 Import</button>
        </div>
      </div>

      {/* Feature list */}
      {features.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">{features.length} Features</div>
          {features.map((f) => (
            <div key={f.id} className="list-item">
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
              <span style={{ fontSize: 16 }}>{TOOLS.find((t) => t.tool === f.type)?.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{f.label}</div>
                {f.feature.properties?.measurements && (
                  <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                    {f.feature.properties.measurements}
                  </div>
                )}
              </div>
              <button className="btn-icon" onClick={() => removeFeature(f.id)} title="Delete" style={{ fontSize: 12 }}>🗑️</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
